import { manualUtf8Encode } from "./encoding.utils";

/**
 * Helper to encode string to Uint8Array in a robust way
 * Tries to use TextEncoder if available, falls back to manual UTF-8 encoding
 */
function encodeToUint8(input: string): Uint8Array {
  try {
    const T = (globalThis as unknown as { TextEncoder?: unknown })
      .TextEncoder as unknown;
    if (typeof T === "function") {
      try {
        const EncoderCtor = T as unknown as {
          new (): { encode(input: string): Uint8Array };
        };
        return new EncoderCtor().encode(input);
      } catch (_) {
        return manualUtf8Encode(input);
      }
    }
  } catch (_) {
    // ignore
  }

  return manualUtf8Encode(input);
}

/**
 * Helper function to anonymize user PII data using SHA-256 hashing
 * @param data - The string data to be anonymized
 * @returns Promise<string> - The SHA-256 hash of the input data
 */
export async function anonymizeData(data: string): Promise<string> {
  if (!data || data.trim() === "") {
    return "";
  }

  try {
    const subtle = (globalThis.crypto as unknown as { subtle?: SubtleCrypto })
      .subtle as SubtleCrypto | undefined;

    if (subtle && typeof subtle.digest === "function") {
      const dataBuffer = encodeToUint8(data);
      const hashBuffer = await subtle.digest("SHA-256", dataBuffer);

      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    }

    return Promise.resolve(anonymizeDataSync(data));
  } catch (error: unknown) {
    if (process.env.NODE_ENV !== "production") {
      if (error instanceof Error) {
        console.debug("Error anonymizing data:", error.message);
      } else {
        console.debug("Error anonymizing data:", String(error));
      }
    }
    return "";
  }
}

/**
 * Synchronous version using a simple hash function (fallback for older browsers)
 * Note: This is less secure than SHA-256 but provides basic anonymization
 * @param data - The string data to be anonymized
 * @returns string - A simple hash of the input data
 */
export function anonymizeDataSync(data: string): string {
  if (!data || data.trim() === "") {
    return "";
  }

  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16);
}
